import Close from '@mui/icons-material/Close'
import LockOutlined from '@mui/icons-material/LockOutlined'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getMPDataWithoutParams } from 'src/mentor/services'
import { selectEmrSectionsData } from 'src/clinic/reducers/prescription/prescriptionReducer'
import { fetchEmrSections } from 'src/clinic/reducers/prescription/services/emrSectionThunk'
import { isCommonResponseSuccessfulV2, getDataMapFromCommonResponse } from 'src/mentor/helpers/utilityFunctions'
import { useBodyScrollLock } from 'src/hooks/useBodyScrollLock'
import PrintableScreeningReportComponent from 'src/mentor/components/ScreeningReportMakingPage/printableReport/PrintableScreeningReportComponent'
import TherapyReportPreview from 'src/mentor/components/TherapyReportMakingPage/components/TherapyReportPreview'
import ViewOnlyPrescriptionV2 from 'src/mentor/components/PrescriptionMakingPage/viewOnlyComponents/ViewOnlyPrescriptionV2'
import AssessmentReportHistoryView from 'src/consumer/components/LiveSessionPage/components/LSPastVisitReportsComponent/AssessmentReportHistoryView'
import { hasMeaningfulPrescriptionData } from 'src/mentor/components/PrescriptionMakingPage/printComponents/utils/emrReportContentUtils'
import { allAvailableEventTypes, BOOKING_SESSION_STATUS, displayNamesOfEventTypes } from 'src/consumer/constants'
import styled from 'styled-components'

dayjs.extend(utc)
dayjs.extend(isSameOrBefore)

function isImageUrl(url) {
  const path = String(url || '').split('?')[0].toLowerCase()
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(path)
}

// Renders the uploaded report PDF(s)/scan(s) (e.g. EkaCare bulk-imported prescriptions, or photos
// of a handwritten report), which the structured renderers do not show.
function UploadedReportFiles({ urls, label = 'Uploaded prescription' }) {
  if (!Array.isArray(urls) || urls.length === 0) {
    return null
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {urls.map((url, idx) => (
        <Box
          key={`${url}-${idx}`}
          sx={{ border: '1px solid rgba(148, 163, 184, 0.3)', borderRadius: 2, overflow: 'hidden' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              backgroundColor: 'rgba(148, 163, 184, 0.08)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {label}{urls.length > 1 ? ` (${idx + 1}/${urls.length})` : ''}
            </Typography>
            <Typography
              component="a"
              href={url}
              target="_blank"
              rel="noreferrer"
              variant="body2"
              sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 600 }}
            >
              Open in new tab
            </Typography>
          </Box>
          {isImageUrl(url) ? (
            <Box component="img" src={url} alt={label} sx={{ width: '100%', display: 'block' }} />
          ) : (
            <Box
              component="iframe"
              src={url}
              title={`${label} ${idx + 1}`}
              sx={{ width: '100%', height: { xs: 480, sm: 720 }, border: 0, display: 'block' }}
            />
          )}
        </Box>
      ))}
    </Box>
  )
}

const DrawerContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
`

const DrawerHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: #ffffff;
`

const DrawerTitle = styled(Typography)`
  font-size: 18px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
`

const DrawerContent = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`

const LoadingContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 16px;
`

const ErrorContainer = styled(Box)`
  padding: 20px;
`

const ReportContainer = styled(Box)`
  width: 100%;
`

function PrivateNotesCard({ note, loading, error }) {
  const noteContent = `${note?.content || ''}`.trim()

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        border: '1px solid rgba(99, 102, 241, 0.22)',
        background: 'linear-gradient(135deg, rgba(238, 242, 255, 0.9) 0%, rgba(255, 255, 255, 0.96) 100%)',
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={0.9} alignItems="center">
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '999px',
                display: 'grid',
                placeItems: 'center',
                color: '#4338ca',
                background: 'rgba(99, 102, 241, 0.12)',
              }}
            >
              <LockOutlined sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                Private Notes
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Practitioner-only notes for this session
              </Typography>
            </Box>
          </Stack>
          <Chip size="small" label="Private" variant="outlined" sx={{ fontWeight: 700 }} />
        </Stack>

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading private notes...
            </Typography>
          </Stack>
        ) : error ? (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : noteContent ? (
          <Typography
            variant="body2"
            sx={{
              color: '#1e293b',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {noteContent}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No private notes were created for this session.
          </Typography>
        )}
      </Stack>
    </Paper>
  )
}

export default function ReportViewDrawer({
  open,
  onClose,
  bookedEvent,
  reportType, // 'prescription', 'therapy-report', 'screening-report', 'assessment-report'
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const dispatch = useDispatch()

  // Lock body scroll when drawer is open
  useBodyScrollLock(open, 1300)

  // The pad layout must come from the SAME source the prescription making page uses —
  // /api/secure/practitioner/clinical/emr-settings/emr-sections via the cached fetchEmrSections
  // thunk (specialization/discipline-aware section resolution).
  const emrSectionsStoreData = useSelector(selectEmrSectionsData)
  const doctorConfiguredEmrSections =
    emrSectionsStoreData?.data?.prescription?.emrSections || null

  useEffect(() => {
    if (open && reportType === 'prescription') {
      dispatch(fetchEmrSections())
    }
  }, [open, reportType, dispatch])
  
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [prescriptionData, setPrescriptionData] = useState(null)
  const [prescriptionExists, setPrescriptionExists] = useState(null)
  const [therapyReportData, setTherapyReportData] = useState(null)
  const [screeningReportData, setScreeningReportData] = useState(null)
  const [assessmentReportData, setAssessmentReportData] = useState(null)
  const [emrSections, setEmrSections] = useState(null)
  const [rxTab, setRxTab] = useState('uploaded')
  const [privateNoteData, setPrivateNoteData] = useState(null)
  const [privateNotesLoading, setPrivateNotesLoading] = useState(false)
  const [privateNotesError, setPrivateNotesError] = useState('')

  // Check if session has started
  const hasSessionStarted = (() => {
    if (bookedEvent?.sessionStatus) {
      return bookedEvent.sessionStatus !== BOOKING_SESSION_STATUS.NOT_STARTED
    }
    if (!bookedEvent?.startTime) {
      return false
    }
    return dayjs.utc(bookedEvent.startTime).local().isSameOrBefore(dayjs())
  })()

  // Get event type display name
  const eventTypeDisplayName = displayNamesOfEventTypes[bookedEvent?.eventType] || 
    (bookedEvent?.eventType === allAvailableEventTypes.screening ? 'Screening' :
     bookedEvent?.eventType === allAvailableEventTypes.therapySession ? 'Therapy Session' :
     bookedEvent?.eventType === allAvailableEventTypes.assessment ? 'Assessment' :
     'Session')

  // Get report type display name
  const getReportTypeDisplayName = () => {
    if (reportType === 'prescription') return 'Prescription'
    if (reportType === 'therapy-report') return 'Therapy Report'
    if (reportType === 'screening-report') return 'Screening Report'
    if (reportType === 'assessment-report') return 'Assessment Report'
    return 'Report'
  }

  // Fetch report data
  useEffect(() => {
    if (!open || !bookedEvent || !hasSessionStarted) {
      return
    }

    const fetchReportData = async () => {
      setLoading(true)
      setError(null)
      setRxTab('uploaded')

      try {
        if (reportType === 'prescription') {
          const response = await getMPDataWithoutParams(
            `/api/secure/doctor/prescription/get-by-eventId?eventId=${bookedEvent.id}`
          )

          if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
            const data = getDataMapFromCommonResponse(response)
            setPrescriptionData(data?.metaPrescription)
            // Backend sends false when no prescription was ever recorded for this event (the
            // metaPrescription is then just an empty initialized pad). Absent on older servers.
            setPrescriptionExists(typeof data?.prescriptionExists === 'boolean' ? data.prescriptionExists : null)
            // doctorEmrSections = the doctor's section LAYOUT (EmrSection defs). data.emrSectionData
            // is the saved per-event section DATA — passing that as the layout renders a blank pad
            // (this was why populated prescriptions showed empty). When the layout is absent,
            // ViewOnlyPrescriptionV2 falls back to its default data-bearing sections.
            setEmrSections(data?.doctorEmrSections || data?.emrSections)
          } else {
            setError('Failed to load prescription data')
          }
        } else if (reportType === 'therapy-report') {
          // Use the same endpoint as screening but for therapy
          const response = await getMPDataWithoutParams(
            `/api/secure/patient/therapy-report/create-by-eventId?eventId=${bookedEvent.id}`
          )
          
          if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
            const data = getDataMapFromCommonResponse(response)
            setTherapyReportData({
              report: data?.therapyReport,
              template: data?.therapyReportTemplate,
            })
          } else {
            setError('Failed to load therapy report data')
          }

          if (bookedEvent?.patientId) {
            setPrivateNotesLoading(true)
            setPrivateNotesError('')
            try {
              const privateNotesResponse = await getMPDataWithoutParams(
                `/api/secure/teacher/private-notes?patientId=${bookedEvent.patientId}`
              )

              if (
                privateNotesResponse?.status === 200 &&
                isCommonResponseSuccessfulV2(privateNotesResponse)
              ) {
                const privateNotesData = getDataMapFromCommonResponse(privateNotesResponse)
                const notesList =
                  privateNotesData?.['mentor-private-notes'] || privateNotesData?.notes || []
                const thisSessionNote = Array.isArray(notesList)
                  ? notesList.find(
                      (item) => `${item?.bookedEventId || ''}` === `${bookedEvent.id || ''}`,
                    )
                  : null
                setPrivateNoteData(thisSessionNote || null)
              } else {
                setPrivateNotesError('Unable to load private notes for this session.')
              }
            } catch (privateNotesFetchError) {
              console.error('Error fetching private notes:', privateNotesFetchError)
              setPrivateNotesError('Unable to load private notes for this session.')
            } finally {
              setPrivateNotesLoading(false)
            }
          }
        } else if (reportType === 'screening-report') {
          const response = await getMPDataWithoutParams(
            `/api/secure/patient/screening-report/create-by-eventId?eventId=${bookedEvent.id}`
          )
          
          if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
            const data = getDataMapFromCommonResponse(response)
            setScreeningReportData({
              report: data?.screeningReport,
              template: data?.screeningReportTemplate,
              emrSectionDataList: data?.emrSectionDataList,
              emrSectionPrintSettings: data?.emrSectionPrintSettings,
              screeningSections: data?.screeningSections,
            })
          } else {
            setError('Failed to load screening report data')
          }
        } else if (reportType === 'assessment-report') {
          const response = await getMPDataWithoutParams(
            `/api/secure/patient/assessment-report/get-by-eventId?eventId=${bookedEvent.id}`
          )

          if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
            const data = getDataMapFromCommonResponse(response)
            setAssessmentReportData({
              report: data?.assessmentReport,
              template: data?.assessmentReportTemplate,
            })
          } else {
            setError('Failed to load assessment report data')
          }
        }
      } catch (err) {
        console.error('Error fetching report data:', err)
        setError('An error occurred while loading the report')
      } finally {
        setLoading(false)
      }
    }

    fetchReportData()
  }, [open, bookedEvent, reportType, hasSessionStarted])

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) {
      setPrescriptionData(null)
      setPrescriptionExists(null)
      setTherapyReportData(null)
      setScreeningReportData(null)
      setAssessmentReportData(null)
      setEmrSections(null)
      setPrivateNoteData(null)
      setPrivateNotesError('')
      setPrivateNotesLoading(false)
      setError(null)
      setLoading(false)
    }
  }, [open])

  const getReportTitle = () => {
    return getReportTypeDisplayName()
  }

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : '900px',
          maxWidth: isMobile ? '100%' : '90vw',
          height: isMobile ? '90vh' : '100%',
          maxHeight: isMobile ? '90vh' : '100%',
        },
      }}
      ModalProps={{
        disableScrollLock: true, // We handle scroll lock with useBodyScrollLock hook
      }}
    >
      <DrawerContainer>
        <DrawerHeader>
          <DrawerTitle>{getReportTitle()}</DrawerTitle>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </DrawerHeader>

        <DrawerContent>
          {!hasSessionStarted ? (
            <ErrorContainer>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This {eventTypeDisplayName.toLowerCase()} has not started yet.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start/complete the session to access {getReportTypeDisplayName().toLowerCase()}.
                </Typography>
              </Alert>
            </ErrorContainer>
          ) : loading ? (
            <LoadingContainer>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary">
                Loading report...
              </Typography>
            </LoadingContainer>
          ) : error ? (
            <ErrorContainer>
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            </ErrorContainer>
          ) : (
            <>
              {reportType === 'prescription' && prescriptionData && (() => {
                const uploadedUrls = prescriptionData?.prescriptionData?.uploadedSignedImages || []
                const hasUploaded = uploadedUrls.length > 0
                const hasGenerated = hasMeaningfulPrescriptionData(prescriptionData?.prescriptionData)
                // Nothing was ever recorded for this appointment (the API returns an empty
                // initialized pad in that case) — say so instead of rendering a blank pad.
                if (!hasUploaded && !hasGenerated) {
                  return (
                    <ErrorContainer>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          No prescription has been recorded for this appointment
                          {prescriptionExists === false ? '' : ' yet'}.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Prescriptions written from the session screen, and any images or PDFs
                          uploaded for this appointment, will appear here.
                        </Typography>
                      </Alert>
                    </ErrorContainer>
                  )
                }
                // Render the same A4 print preview the making page's Quick Print drawer shows
                // (template letterhead + margins + paginated pages), not a flat section dump.
                const generatedPad = (
                  <Box sx={{ overflowX: 'auto', backgroundColor: '#f5f5f5', p: 2, borderRadius: 2 }}>
                    <Box
                      sx={{
                        minWidth: '750px',
                        margin: '0 auto',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    >
                      <ViewOnlyPrescriptionV2
                        prescriptionData={prescriptionData}
                        doctorEmrSections={
                          doctorConfiguredEmrSections?.length ? doctorConfiguredEmrSections : emrSections
                        }
                        showTheA4Page
                        hidePrintMarginsControl
                      />
                    </Box>
                  </Box>
                )
                if (!hasUploaded) {
                  return <ReportContainer>{generatedPad}</ReportContainer>
                }
                // Uploaded-only record (no structured prescription data): show just the uploaded
                // document — don't surface an empty Generated tab.
                if (!hasGenerated) {
                  return (
                    <ReportContainer>
                      <UploadedReportFiles urls={uploadedUrls} />
                    </ReportContainer>
                  )
                }
                const activeTab = rxTab === 'generated' ? 'generated' : 'uploaded'
                return (
                  <ReportContainer>
                    <Tabs
                      value={activeTab}
                      onChange={(_, value) => setRxTab(value)}
                      sx={{ mb: 2, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
                    >
                      <Tab value="uploaded" label="Uploaded" />
                      <Tab value="generated" label="Generated" />
                    </Tabs>
                    {activeTab === 'uploaded'
                      ? <UploadedReportFiles urls={uploadedUrls} />
                      : generatedPad}
                  </ReportContainer>
                )
              })()}
              {reportType === 'therapy-report' && therapyReportData && (
                <ReportContainer>
                  <PrivateNotesCard
                    note={privateNoteData}
                    loading={privateNotesLoading}
                    error={privateNotesError}
                  />
                  <TherapyReportPreview
                    hideInUI={false}
                    isAlwaysPrinting
                    report={therapyReportData?.report}
                    templateData={therapyReportData?.template}
                    setReport={() => {}}
                    hideDownloadButton={true}
                    hideSendToDoctorButton={true}
                    hideHeader={true}
                    hideHeadingText={true}
                  />
                  <Box sx={{ mt: 2 }}>
                    <UploadedReportFiles
                      urls={therapyReportData?.report?.uploadedSignedImages}
                      label="Uploaded report"
                    />
                  </Box>
                </ReportContainer>
              )}
              {reportType === 'screening-report' && screeningReportData && (
                <ReportContainer>
                  <PrintableScreeningReportComponent
                    emrSectionDataList={screeningReportData?.emrSectionDataList}
                    bookedEventInfo={bookedEvent}
                    templateData={screeningReportData?.template}
                    isAlwaysPrinting
                    report={screeningReportData?.report}
                    hideDownloadButton={true}
                    hideSendToDoctorButton={true}
                    hideHeader={true}
                    hideHeadingText={true}
                    screeningSections={screeningReportData?.screeningSections}
                  />
                  <Box sx={{ mt: 2 }}>
                    <UploadedReportFiles
                      urls={screeningReportData?.report?.uploadedSignedImages}
                      label="Uploaded report"
                    />
                  </Box>
                </ReportContainer>
              )}
              {reportType === 'assessment-report' && assessmentReportData && (() => {
                const report = assessmentReportData?.report
                const uploadedUrls =
                  report?.uploadedReportSignedImages || report?.uploadedSignedImages || []
                const hasRuns =
                  Array.isArray(report?.assessmentRuns) && report.assessmentRuns.length > 0
                const hasNarrative = Boolean(
                  `${report?.freeFormRichText || ''}`.trim() || `${report?.content || ''}`.trim(),
                )
                if (!hasRuns && !hasNarrative && uploadedUrls.length === 0) {
                  return (
                    <ErrorContainer>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          No assessment report has been recorded for this appointment yet.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Assessments conducted during the session, and any uploaded report files,
                          will appear here.
                        </Typography>
                      </Alert>
                    </ErrorContainer>
                  )
                }
                return (
                  <ReportContainer>
                    {(hasRuns || hasNarrative) && (
                      <AssessmentReportHistoryView
                        report={{ assessmentReport: report, assessmentRuns: report?.assessmentRuns }}
                      />
                    )}
                    <Box sx={{ mt: 2 }}>
                      <UploadedReportFiles urls={uploadedUrls} label="Uploaded report" />
                    </Box>
                  </ReportContainer>
                )
              })()}
              {!prescriptionData &&
                !therapyReportData &&
                !screeningReportData &&
                !assessmentReportData &&
                !loading && (
                <ErrorContainer>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No report available for this appointment.
                  </Alert>
                </ErrorContainer>
              )}
            </>
          )}
        </DrawerContent>
      </DrawerContainer>
    </Drawer>
  )
}
